import {
  CLINICAL_NOTES_AES_GCM_IV_LENGTH,
  CLINICAL_NOTES_CRYPTO_KDF_PARAMS_V1,
  CLINICAL_NOTES_CRYPTO_SCHEMA_VERSION,
  CLINICAL_NOTES_KDF_SALT_LENGTH,
  CLINICAL_NOTES_MASTER_KEY_LENGTH,
  CLINICAL_NOTES_PIN_LENGTH,
  CLINICAL_NOTES_RECOVERY_CODE_ALPHABET,
  CLINICAL_NOTES_RECOVERY_CODE_LENGTH
} from '@/config/constants'
import { base64UrlToBytes, bytesToBase64Url } from '@/modules/psychology/clinical-notes/utils/base64url'

const AES_GCM = 'AES-GCM'
const AES_KEY_LENGTH = 256

function assertValidPin(pin) {
  const s = String(pin ?? '')
  if (!/^\d+$/.test(s) || s.length !== CLINICAL_NOTES_PIN_LENGTH) {
    throw new Error('clinicalNotesCrypto: PIN inválido')
  }
}

function assertEnvelopesPayload(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('clinicalNotesCrypto: payload inválido')
  }
  const kdf = data.kdf
  const pinEnv = data.pin_envelope
  if (
    !kdf ||
    typeof kdf.pin_salt_b64 !== 'string' ||
    typeof kdf.iterations !== 'number' ||
    typeof kdf.hash !== 'string' ||
    typeof kdf.param_version !== 'number'
  ) {
    throw new Error('clinicalNotesCrypto: kdf incompleto')
  }
  if (
    !pinEnv ||
    typeof pinEnv.iv_b64 !== 'string' ||
    typeof pinEnv.ciphertext_b64 !== 'string'
  ) {
    throw new Error('clinicalNotesCrypto: pin_envelope incompleto')
  }
}

function assertSupportedClinicalNotesEnvelopesVersion(data) {
  if (Number(data?.encryption_version) !== CLINICAL_NOTES_CRYPTO_SCHEMA_VERSION) {
    throw new Error('clinicalNotesCrypto: encryption_version no soportada')
  }
  if (Number(data?.kdf?.param_version) !== CLINICAL_NOTES_CRYPTO_KDF_PARAMS_V1.version) {
    throw new Error('clinicalNotesCrypto: param_version KDF no soportada')
  }
}

export function generateRecoveryCode() {
  const alphabet = CLINICAL_NOTES_RECOVERY_CODE_ALPHABET
  const n = alphabet.length
  const max = 256 - (256 % n)
  let result = ''

  for (let i = 0; i < CLINICAL_NOTES_RECOVERY_CODE_LENGTH; i++) {
    let x
    do {
      x = crypto.getRandomValues(new Uint8Array(1))[0]
    } while (x >= max)
    result += alphabet[x % n]
  }

  return result
}

export function normalizeRecoveryCodeInput(input) {
  return String(input ?? '').replace(/[\s-]/g, '')
}

async function deriveAesGcm256Key(secretUtf8, salt, kdfParams) {
  const enc = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(secretUtf8),
    'PBKDF2',
    false,
    ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: kdfParams.iterations,
      hash: kdfParams.hash
    },
    keyMaterial,
    { name: AES_GCM, length: AES_KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  )
}

async function aesGcmEncrypt(key, plaintext) {
  const iv = crypto.getRandomValues(new Uint8Array(CLINICAL_NOTES_AES_GCM_IV_LENGTH))
  const ciphertext = await crypto.subtle.encrypt(
    { name: AES_GCM, iv },
    key,
    plaintext
  )
  return { iv, ciphertext: new Uint8Array(ciphertext) }
}

async function aesGcmDecrypt(key, iv, ciphertext) {
  const plaintext = await crypto.subtle.decrypt(
    { name: AES_GCM, iv },
    key,
    ciphertext
  )
  return new Uint8Array(plaintext)
}

export async function prepareClinicalNotesCryptoEnrollment(pin) {
  assertValidPin(pin)

  const kdf = CLINICAL_NOTES_CRYPTO_KDF_PARAMS_V1
  const kdfParams = {
    iterations: kdf.iterations,
    hash: kdf.hash
  }
  const masterKeyBytes = crypto.getRandomValues(new Uint8Array(CLINICAL_NOTES_MASTER_KEY_LENGTH))
  const recoveryCode = generateRecoveryCode()
  const pinSalt = crypto.getRandomValues(new Uint8Array(CLINICAL_NOTES_KDF_SALT_LENGTH))
  const recoverySalt = crypto.getRandomValues(new Uint8Array(CLINICAL_NOTES_KDF_SALT_LENGTH))

  const pinKey = await deriveAesGcm256Key(pin, pinSalt, kdfParams)
  const recoveryKey = await deriveAesGcm256Key(recoveryCode, recoverySalt, kdfParams)
  const pinWrap = await aesGcmEncrypt(pinKey, masterKeyBytes)
  const recoveryWrap = await aesGcmEncrypt(recoveryKey, masterKeyBytes)

  return {
    requestBody: {
      encryption_version: CLINICAL_NOTES_CRYPTO_SCHEMA_VERSION,
      kdf: {
        param_version: kdf.version,
        algorithm: `${kdf.name}-${kdf.hash}`,
        iterations: kdf.iterations,
        hash: kdf.hash,
        pin_salt_b64: bytesToBase64Url(pinSalt),
        recovery_salt_b64: bytesToBase64Url(recoverySalt)
      },
      pin_envelope: {
        iv_b64: bytesToBase64Url(pinWrap.iv),
        ciphertext_b64: bytesToBase64Url(pinWrap.ciphertext)
      },
      recovery_envelope: {
        iv_b64: bytesToBase64Url(recoveryWrap.iv),
        ciphertext_b64: bytesToBase64Url(recoveryWrap.ciphertext)
      }
    },
    recoveryCode,
    masterKeyBytes
  }
}

export async function unlockMasterKeyWithPin(pin, data) {
  assertValidPin(pin)
  assertEnvelopesPayload(data)
  assertSupportedClinicalNotesEnvelopesVersion(data)

  const pinSalt = base64UrlToBytes(data.kdf.pin_salt_b64)
  const iv = base64UrlToBytes(data.pin_envelope.iv_b64)
  const ciphertext = base64UrlToBytes(data.pin_envelope.ciphertext_b64)
  if (!pinSalt || pinSalt.length !== CLINICAL_NOTES_KDF_SALT_LENGTH) {
    throw new Error('clinicalNotesCrypto: pin_salt inválido')
  }
  if (!iv || iv.length !== CLINICAL_NOTES_AES_GCM_IV_LENGTH) {
    throw new Error('clinicalNotesCrypto: iv inválido')
  }
  if (!ciphertext || ciphertext.length < 16) {
    throw new Error('clinicalNotesCrypto: ciphertext inválido')
  }

  const pinKey = await deriveAesGcm256Key(pin, pinSalt, {
    iterations: data.kdf.iterations,
    hash: data.kdf.hash
  })

  try {
    const masterKeyBytes = await aesGcmDecrypt(pinKey, iv, ciphertext)
    if (masterKeyBytes.length !== CLINICAL_NOTES_MASTER_KEY_LENGTH) {
      throw new Error('clinicalNotesCrypto: longitud de master_key inesperada')
    }
    return masterKeyBytes
  } catch (_) {
    throw new Error('Pin incorrecto')
  }
}

export async function unlockMasterKeyWithRecoveryCode(recoveryCode, data) {
  const code = normalizeRecoveryCodeInput(recoveryCode)
  if (!code || code.length !== CLINICAL_NOTES_RECOVERY_CODE_LENGTH) {
    throw new Error('clinicalNotesCrypto: código de recuperación inválido')
  }
  assertEnvelopesPayload(data)
  assertSupportedClinicalNotesEnvelopesVersion(data)

  const recEnv = data.recovery_envelope
  const recoverySaltB64 = data.kdf?.recovery_salt_b64
  if (
    !recEnv ||
    typeof recEnv.iv_b64 !== 'string' ||
    typeof recEnv.ciphertext_b64 !== 'string' ||
    typeof recoverySaltB64 !== 'string'
  ) {
    throw new Error('clinicalNotesCrypto: recovery incompleto')
  }

  const recoverySalt = base64UrlToBytes(recoverySaltB64)
  const iv = base64UrlToBytes(recEnv.iv_b64)
  const ciphertext = base64UrlToBytes(recEnv.ciphertext_b64)
  if (!recoverySalt || recoverySalt.length !== CLINICAL_NOTES_KDF_SALT_LENGTH) {
    throw new Error('clinicalNotesCrypto: recovery_salt inválido')
  }
  if (!iv || iv.length !== CLINICAL_NOTES_AES_GCM_IV_LENGTH) {
    throw new Error('clinicalNotesCrypto: iv recuperación inválido')
  }
  if (!ciphertext || ciphertext.length < 16) {
    throw new Error('clinicalNotesCrypto: ciphertext recuperación inválido')
  }

  const recoveryKey = await deriveAesGcm256Key(code, recoverySalt, {
    iterations: data.kdf.iterations,
    hash: data.kdf.hash
  })

  try {
    const masterKeyBytes = await aesGcmDecrypt(recoveryKey, iv, ciphertext)
    if (masterKeyBytes.length !== CLINICAL_NOTES_MASTER_KEY_LENGTH) {
      throw new Error('clinicalNotesCrypto: longitud de master_key inesperada')
    }
    return masterKeyBytes
  } catch (_) {
    throw new Error('Código incorrecto')
  }
}

export async function encryptClinicalNotePlaintextWithMasterKey(
  masterKeyBytes,
  plaintextUtf8
) {
  if (!masterKeyBytes || masterKeyBytes.length !== CLINICAL_NOTES_MASTER_KEY_LENGTH) {
    throw new Error('clinicalNotesCrypto: master_key inválida')
  }

  const key = await crypto.subtle.importKey(
    'raw',
    masterKeyBytes,
    { name: AES_GCM, length: AES_KEY_LENGTH },
    false,
    ['encrypt']
  )
  const enc = new TextEncoder()
  const plaintext = enc.encode(String(plaintextUtf8 ?? ''))
  const { iv, ciphertext } = await aesGcmEncrypt(key, plaintext)

  return {
    encryption_version: CLINICAL_NOTES_CRYPTO_SCHEMA_VERSION,
    notes_iv_b64: bytesToBase64Url(iv),
    notes_ciphertext_b64: bytesToBase64Url(ciphertext)
  }
}

export async function decryptClinicalNotePlaintextWithMasterKey(masterKeyBytes, payload) {
  if (!masterKeyBytes || masterKeyBytes.length !== CLINICAL_NOTES_MASTER_KEY_LENGTH) {
    throw new Error('clinicalNotesCrypto: master_key inválida')
  }
  const ev = payload?.encryption_version
  if (ev != null && Number(ev) !== CLINICAL_NOTES_CRYPTO_SCHEMA_VERSION) {
    throw new Error('clinicalNotesCrypto: encryption_version no soportada')
  }
  const ivB64 = payload?.notes_iv_b64
  const cipherB64 = payload?.notes_ciphertext_b64 ?? payload?.notes
  if (typeof ivB64 !== 'string' || typeof cipherB64 !== 'string') {
    throw new Error('clinicalNotesCrypto: payload de nota incompleto')
  }
  const iv = base64UrlToBytes(ivB64)
  const ciphertext = base64UrlToBytes(cipherB64)
  if (!iv || iv.length !== CLINICAL_NOTES_AES_GCM_IV_LENGTH) {
    throw new Error('clinicalNotesCrypto: iv de nota inválido')
  }
  if (!ciphertext || ciphertext.length < 16) {
    throw new Error('clinicalNotesCrypto: ciphertext de nota inválido')
  }

  const key = await crypto.subtle.importKey(
    'raw',
    masterKeyBytes,
    { name: AES_GCM, length: AES_KEY_LENGTH },
    false,
    ['decrypt']
  )

  try {
    const decrypted = await crypto.subtle.decrypt({ name: AES_GCM, iv }, key, ciphertext)
    return new TextDecoder().decode(decrypted)
  } catch (_) {
    throw new Error('clinicalNotesCrypto: no se pudo descifrar la nota')
  }
}
